// Neutral auth surface. Nothing outside lib/auth may import supabase-js
// (enforced by ESLint no-restricted-imports); swapping providers means
// writing another implementation of this interface, not touching screens.

export type AuthSession = {
  accessToken: string;
  userId: string;
  email: string | null;
};

// INITIAL_SESSION fires once on subscribe with the stored session (or null);
// OTHER covers provider events nothing consumes (e.g. USER_UPDATED).
export type AuthEvent =
  'INITIAL_SESSION' | 'SIGNED_IN' | 'SIGNED_OUT' | 'TOKEN_REFRESHED' | 'PASSWORD_RECOVERY' | 'OTHER';

export type OAuthProvider = 'google' | 'apple';

export class AuthError extends Error {
  readonly code: string | null;

  constructor(message: string, code: string | null = null) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
  }
}

// Failing methods reject with AuthError.
export interface AuthClient {
  getSession(): Promise<AuthSession | null>;
  // Returns an unsubscribe function.
  onAuthChange(callback: (event: AuthEvent, session: AuthSession | null) => void): () => void;
  signInWithPassword(email: string, password: string): Promise<void>;
  // session is null when the provider requires email confirmation first.
  signUp(email: string, password: string): Promise<{ session: AuthSession | null }>;
  signInWithOAuth(provider: OAuthProvider, redirectTo?: string): Promise<void>;
  // Best-effort: never rejects on provider-side errors (callers only care
  // that local state is cleared, which drives the nav gate).
  signOut(): Promise<void>;
  // null when the refresh failed.
  refreshSession(): Promise<AuthSession | null>;
  requestPasswordReset(email: string, redirectTo?: string): Promise<void>;
  updatePassword(password: string): Promise<void>;
}
